import { lazy, Suspense, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import AlertsSection from "../components/AlertsSection";
import AppBar from "../components/AppBar";
import CurrentConditions from "../components/CurrentConditions";
import CurrentConditionsSkeleton from "../components/CurrentConditionsSkeleton";
import DayCard from "../components/DayCard";
import DayCardSkeleton from "../components/DayCardSkeleton";
import ErrorBanner from "../components/ErrorBanner";
import ForecastError from "../components/ForecastError";
import { PageShell, PageShellColumn } from "../components/PageShell";
import StaleBanner from "../components/StaleBanner";
import { useAlerts } from "../hooks/useAlerts";
import { useForecast } from "../hooks/useForecast";
import { useSelectedLocation } from "../hooks/useSelectedLocation";
import { formatCoordinates, formatLocationLabel, formatNwsUpdateTime } from "../lib/format";

const LocationDrawer = lazy(() => import("../components/LocationDrawer"));

function ForecastDayListSkeleton() {
  return (
    <div aria-busy="true" aria-label="Loading daily forecasts" className="flex flex-col gap-3">
      {Array.from({ length: 4 }, (_, i) => (
        <DayCardSkeleton key={i} />
      ))}
    </div>
  );
}

function UpcomingForecastHeader() {
  return (
    <>
      <h2 className="mb-1 text-[24px] font-bold">Upcoming forecast</h2>
      <Link to="/help/timeline" className="mb-3 text-[12px] text-primary">
        How to read the timeline
      </Link>
    </>
  );
}

export default function HomeForecastPage() {
  const navigate = useNavigate();
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const {
    data: forecast,
    isPending,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useForecast();
  const { data: alertsData, isError: alertsIsError } = useAlerts();
  const { location } = useSelectedLocation();

  const isStale = !isError && forecast?.meta.stale === true;
  const showFullError = isError && !forecast && !isFetching;
  const showLoadingShell = !forecast && !showFullError && (isPending || isLoading || isFetching);

  const hideLocationSubLabel = (isPending || isLoading) && !forecast;
  const locationLabel = hideLocationSubLabel
    ? null
    : formatLocationLabel(forecast?.location.place, location);
  const coordinates = formatCoordinates(location.lat, location.lon);

  const hasAlerts =
    alertsIsError || (alertsData?.alerts !== undefined && alertsData.alerts.length > 0);

  return (
    <div className="flex min-h-dvh flex-col bg-surface text-on-surface">
      <AppBar
        onMenuClick={() => setDrawerOpen(true)}
        locationLabel={locationLabel}
        coordinates={coordinates}
      />

      <PageShell split>
        {isStale && forecast ? (
          <PageShellColumn fullWidth>
            <StaleBanner
              message="Showing last known forecast — couldn't reach the National Weather Service."
              generatedAt={forecast.meta.generatedAt}
            />
          </PageShellColumn>
        ) : null}

        {isError && forecast ? (
          <PageShellColumn fullWidth>
            <ErrorBanner
              message="Couldn't refresh — showing last known forecast. Tap to retry."
              onRetry={() => {
                void refetch();
              }}
            />
          </PageShellColumn>
        ) : null}

        {showLoadingShell ? (
          <>
            <PageShellColumn sticky>
              <CurrentConditionsSkeleton />
            </PageShellColumn>
            <PageShellColumn>
              <UpcomingForecastHeader />
              <ForecastDayListSkeleton />
            </PageShellColumn>
          </>
        ) : null}

        {showFullError ? (
          <PageShellColumn fullWidth>
            <ForecastError
              error={error}
              onRetry={() => {
                void refetch();
              }}
            />
          </PageShellColumn>
        ) : null}

        {forecast && !showLoadingShell && !showFullError ? (
          <>
            {hasAlerts ? (
              <PageShellColumn fullWidth>
                <AlertsSection
                  alerts={alertsData?.alerts ?? []}
                  meta={alertsData?.meta}
                  isError={alertsIsError}
                />
              </PageShellColumn>
            ) : null}

            <PageShellColumn sticky>
              <main className="flex flex-col">
                <CurrentConditions data={forecast.current} />
                <p className="mt-2 px-1 text-[12px] text-on-surface-variant">
                  Updated from NWS at{" "}
                  {formatNwsUpdateTime(forecast.meta.generatedAt, forecast.location.timezone)} via
                  tatterCast API
                </p>
              </main>
            </PageShellColumn>

            <PageShellColumn>
              <UpcomingForecastHeader />
              <div className="flex flex-col gap-3">
                {forecast.periods.map((p) => (
                  <DayCard
                    key={`${p.date}-${p.label}`}
                    period={p}
                    onSelect={(date) => navigate(`/day/${date}`)}
                  />
                ))}
              </div>
            </PageShellColumn>
          </>
        ) : null}
      </PageShell>

      <Suspense fallback={null}>
        <LocationDrawer isOpen={isDrawerOpen} onClose={() => setDrawerOpen(false)} />
      </Suspense>
    </div>
  );
}
